import React from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoClip } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoFit } from 'fym-shared/src/music24/musicVideoLook';
import { TypedMemo } from '../../common/types';
import { updateClip } from '../doc/clipOps';
import type { FootageView } from '../footage/footageView';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { Segmented } from '../panels/Segmented';
import { cropBandShare, cropFromPointer } from './cropBand';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "Fit the 16:9 frame" of a vertical clip (design c3): Fill shows a band of the clip (drag the box
 * to pick it, `cropY`); Fit with blur shows the whole clip over a blurred copy.
 */
const ClipFitPickerComponent: React.FC<{ clip: MusicVideoClip; view: FootageView }> = ({ clip, view }) => {
  const intl = useIntl();
  const { change, readOnly } = useMusicVideoEditor();
  const width = view.width ?? 9;
  const height = view.height ?? 16;
  const share = cropBandShare(width, height);
  // The picker shows the whole clip in a 16:9 box (contained): the clip takes the middle of it.
  const side = `${((1 - Math.min(1, width / height / (16 / 9))) / 2) * 100}%`;
  const setFromPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const cropY = cropFromPointer((event.clientY - rect.top) / Math.max(1, rect.height), share);
    change((doc) => updateClip(doc, clip.id, { cropY }), `crop:${clip.id}`);
  };

  return (
    <div className={styles.column}>
      <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.fit)}</span>
      <Segmented<MusicVideoFit>
        label={intl.formatMessage(panelMessages.fit)}
        value={clip.fit}
        disabled={readOnly}
        options={[
          { value: 'fill', label: intl.formatMessage(panelMessages.fitFill) },
          { value: 'blur', label: intl.formatMessage(panelMessages.fitBlur) },
        ]}
        onChange={(fit) => change((doc) => updateClip(doc, clip.id, { fit }))}
      />
      {clip.fit === 'fill' && (
        <div
          className={styles.fitFrame}
          style={view.posterUrl === null ? undefined : { backgroundImage: `url("${view.posterUrl}")` }}
          onPointerDown={(event) => {
            if (readOnly) {
              return;
            }
            event.currentTarget.setPointerCapture(event.pointerId);
            setFromPointer(event);
          }}
          onPointerMove={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              setFromPointer(event);
            }
          }}
        >
          <div
            className={styles.fitBox}
            style={{ left: side, right: side, top: `${clip.cropY * (1 - share) * 100}%`, height: `${share * 100}%` }}
          />
          <span className={styles.fitArrow}>{intl.formatMessage(panelMessages.cropHint)}</span>
        </div>
      )}
      <p className={styles.hint}>{intl.formatMessage(panelMessages.fitHint)}</p>
    </div>
  );
};

export const ClipFitPicker = TypedMemo(ClipFitPickerComponent);
